import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Collecting Data",
  examWeight: "12–15%",
  focus: "Study Design, Bias, Experimental Design",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Introducing Statistics: Do the Data We Collected Tell the Truth?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Core Problem: Variation and Bias</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The fundamental goal of statistics is to use data to learn about the world. However, data varies.
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <KeyTerm 
                term="Population" 
                definition="The entire group of individuals we want information about."
              />
              <KeyTerm 
                term="Sample" 
                definition="A subset of individuals in the population from which we actually collect data."
              />
              <KeyTerm 
                term="Inference" 
                definition="Drawing conclusions about a population based on a sample."
              />
            </div>
          </section>

          <ImportantNote>
            <strong>The Golden Rule of Data Collection:</strong> If data are collected improperly (e.g., using a biased method), no amount of statistical analysis can fix it. Bad data yields bad conclusions. The method used to collect data determines what conclusions we can draw (generalized to the population vs. cause-and-effect).
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Introduction to Planning a Study",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            There are two primary ways to collect data: <strong>Observational Studies</strong> and <strong>Experiments</strong>.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Observational Studies</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In an observational study, researchers measure variables of interest but <strong>do not influence the responses</strong>. They merely "observe."
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Goal:</strong> To describe some group or situation, compare groups, or examine relationships between variables.</li>
              <li><strong>Retrospective:</strong> Examining existing data (e.g., looking at past medical records).</li>
              <li><strong>Prospective:</strong> Tracking individuals into the future (e.g., following a cohort of smokers for 20 years).</li>
            </ul>
            <ImportantNote>
              <strong>Limitation:</strong> Observational studies <strong>cannot</strong> establish cause-and-effect relationships because of <em>confounding variables</em>. They can only show correlation/association.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Experiments</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In an experiment, researchers <strong>deliberately impose some treatment</strong> on individuals to measure their responses.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Goal:</strong> To determine if a treatment causes a change in the response.</li>
              <li><strong>Strength:</strong> Well-designed experiments are the <em>only</em> source of convincing data for <strong>cause-and-effect</strong> relationships.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Census vs. Sample Survey</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Census</h4>
                <p className="text-sm text-slate-700 mt-1">Collects data from <em>every</em> individual in the population.</p>
                <p className="text-sm text-amber-700 mt-2"><em>Issues:</em> Often impossible, too expensive, or takes too long.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Sample Survey</h4>
                <p className="text-sm text-slate-700 mt-1">A type of observational study that collects data from a representative part of the population to estimate population parameters.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Random Sampling and Data Collection",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            To make a sample representative of the population, we must use <strong>chance</strong>.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Simple Random Sample (SRS)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">This is the gold standard for sampling.</p>
            <KeyTerm 
              term="Simple Random Sample (SRS)" 
              definition="An SRS of size n is chosen in such a way that every group of n individuals in the population has an equal chance to be selected as the sample."
            />
            <div className="mt-4 space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-2">How to implement (The Hat Method)</h4>
                <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-700">
                  <li>Write names of the population on identical slips of paper.</li>
                  <li>Put them in a hat and mix well.</li>
                  <li>Draw <InlineMath math="n" /> slips without looking.</li>
                </ol>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-2">How to implement (Technology)</h4>
                <p className="text-sm text-slate-700">Assign a distinct number to every individual. Use a random number generator to select <InlineMath math="n" /> unique integers.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Stratified Random Sample ("Some from All")</h3>
            <Mnemonic title="Some from All" content="In Stratified sampling, you take SOME individuals from ALL groups (strata)." />
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
              <li><strong>Method:</strong> Classify the population into groups of similar individuals (called <strong>strata</strong>). Then choose a separate SRS in each stratum and combine these SRSs to form the full sample.</li>
              <li><strong>Purpose:</strong> Reduces variability. If you think a specific characteristic (like gender or grade level) affects the response, stratifying ensures all groups are represented.</li>
              <li><strong>Example:</strong> To survey a high school about school spirit, split the school into Freshmen, Sophomores, Juniors, Seniors (strata), then take an SRS of 25 students from each grade.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Cluster Sample ("All from Some")</h3>
            <Mnemonic title="All from Some" content="In Cluster sampling, you take ALL individuals from SOME groups (clusters)." />
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
              <li><strong>Method:</strong> Classify the population into groups that are located near each other (called <strong>clusters</strong>). Ideally, clusters are heterogeneous (mini-representations of the population). Choose an SRS of <em>clusters</em>, then include <em>all</em> individuals from the chosen clusters in the sample.</li>
              <li><strong>Purpose:</strong> Efficiency and cost-saving.</li>
              <li><strong>Example:</strong> To survey a high school, select 5 Homerooms (clusters) at random, and survey <em>every</em> student in those 5 rooms.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Systematic Random Sample</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Method:</strong> Select every <InlineMath math="k^{th}" /> individual from a list.</li>
              <li><strong>Requirement:</strong> You must select a random starting point between 1 and <InlineMath math="k" />.</li>
              <li><strong>Example:</strong> Survey every 10th person to enter a stadium, starting with the 4th person.</li>
            </ul>
          </section>

          <ImportantNote>
            <strong>Key Distinction:</strong> In Stratified, you take a sample from <em>every</em> group. In Cluster, you take <em>every</em> person from a <em>sample</em> of groups.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Potential Problems with Sampling",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            <strong>Bias</strong> is the bane of sampling. A method is biased if it systematically favors certain outcomes.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sources of Bias</h3>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">1. Undercoverage</h4>
                <p className="text-sm text-slate-700 mt-1">Occurs when some members of the population cannot be chosen in a sample.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Example:</em> Surveying households via landline phones misses people who only own cell phones (often younger demographics).</p>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">2. Nonresponse</h4>
                <p className="text-sm text-slate-700 mt-1">Occurs when an individual chosen for the sample can't be contacted or refuses to participate.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Note:</em> This is different from voluntary response. In nonresponse, the researcher <em>tried</em> to contact them.</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Response Bias</h4>
                <p className="text-sm text-slate-700 mt-1">A systematic pattern of incorrect responses.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Causes:</em> Leading questions, the gender/appearance of the interviewer, or social desirability (lying about washing hands or drug use).</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">4. Voluntary Response Bias</h4>
                <p className="text-sm text-slate-700 mt-1">Occurs when the sample consists of people who <em>choose</em> themselves by responding to a general appeal.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Result:</em> Usually attracts people with strong, negative opinions (e.g., Yelp reviews, call-in radio shows).</p>
              </div>
            </div>
          </section>

          <ImportantNote>
            <strong>AP Exam Tip on Bias:</strong> Never just say "the data is biased." You must explain:
            <ol className="list-decimal pl-5 mt-2">
              <li>The <strong>direction</strong> of the bias (is the estimate likely too high or too low?).</li>
              <li><strong>Why</strong> it is biased (link the mechanism to the variable).</li>
            </ol>
            <p className="mt-2"><em>Example:</em> "Because the survey was voluntary, people with strong negative opinions are overrepresented, likely leading to an <em>underestimate</em> of customer satisfaction."</p>
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Introduction to Experimental Design",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Vocabulary</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <KeyTerm 
                term="Experimental Units" 
                definition="The smallest collection of individuals to which treatments are applied (humans are often called subjects)."
              />
              <KeyTerm 
                term="Explanatory Variable (Factor)" 
                definition="The variable manipulated by the researcher (e.g., amount of water)."
              />
              <KeyTerm 
                term="Levels" 
                definition="The specific values of the explanatory variable (e.g., 10ml, 20ml, 30ml)."
              />
              <KeyTerm 
                term="Treatments" 
                definition="A specific condition applied to the individuals. If there is one factor, treatments = levels. If there are multiple factors, treatments are the combinations of levels."
              />
              <KeyTerm 
                term="Response Variable" 
                definition="The outcome measured (e.g., plant height)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Confounding Variables</h3>
            <KeyTerm 
              term="Confounding Variable" 
              definition="A variable that is related to the explanatory variable and influences the response variable. It makes it impossible to distinguish the effect of the treatment from the effect of the confounding variable."
            />
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mt-4">
              <h4 className="font-bold text-amber-900">Example</h4>
              <p className="text-sm text-slate-700 mt-1">Testing a new fertilizer on plants in a sunny window vs. old fertilizer on plants in a dark corner. If the new fertilizer plants grow better, was it the fertilizer or the sunlight? <strong>Sunlight is the confounding variable.</strong></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Principles of Experimental Design</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Comparison:</strong> Use a design that compares two or more treatments.</li>
              <li><strong>Random Assignment:</strong> Use chance to assign experimental units to treatments. This creates roughly equivalent groups by balancing lurking variables.</li>
              <li><strong>Control:</strong> Keep other variables that might affect the response the same for all groups (to prevent confounding).</li>
              <li><strong>Replication:</strong> Use enough experimental units in each group so that any differences in the effects of the treatments can be distinguished from chance differences between the groups.</li>
            </ol>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Selecting an Experimental Design",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Completely Randomized Design</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The treatments are assigned to all the experimental units completely by chance.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700 font-mono">
                Units → Random Assignment → Group 1 / Group 2 → Treatment 1 / Treatment 2 → Compare Response
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Randomized Block Design</h3>
            <KeyTerm 
              term="Block" 
              definition="A group of experimental units that are known before the experiment to be similar in some way that is expected to affect the response to the treatments."
            />
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
              <li><strong>Process:</strong> Separate units into blocks (e.g., Men vs. Women), then randomly assign treatments <em>within</em> each block.</li>
              <li><strong>Why?</strong> "Control what you can, block what you can't, randomize the rest." Blocking reduces variability (standard deviation) in the response variable, making it easier to detect a treatment effect.</li>
            </ul>
            <Mnemonic title="Blocking vs. Stratifying" content="Blocking is to experiments what stratifying is to sampling." />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Matched Pairs Design</h3>
            <p className="text-slate-700 leading-relaxed mb-4">A common type of blocked design for comparing <strong>two</strong> treatments.</p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Method A</h4>
                <p className="text-sm text-slate-700 mt-1">Each subject receives <em>both</em> treatments in a random order. (Each subject serves as their own control).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Method B</h4>
                <p className="text-sm text-slate-700 mt-1">Subjects are paired based on similarity (e.g., twins, or two people with same age/weight). One is randomly assigned Treatment A, the other Treatment B.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Control Groups and Blinding</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Control Group:</strong> A group used to provide a baseline for comparing the effects of other treatments. Usually given an inactive treatment (<strong>placebo</strong>), an active treatment, or no treatment at all.</li>
              <li><strong>Placebo Effect:</strong> The response to a dummy treatment.</li>
              <li><strong>Single-Blind:</strong> The subjects do not know which treatment they are receiving.</li>
              <li><strong>Double-Blind:</strong> Neither the subjects nor those who interact with them and measure the response variable know which treatment a subject received. This prevents experimenter bias.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-7",
      title: "Topic 3.7: Inference and Experiments",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Scope of Inference (The Grid)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The conclusions you can draw depend on the design of the study.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-2 text-left"></th>
                    <th className="border border-slate-300 p-2 text-left">Randomly Selected?</th>
                    <th className="border border-slate-300 p-2 text-left">NOT Randomly Selected?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-300 p-2 font-semibold bg-slate-50">Randomly Assigned?</td>
                    <td className="border border-slate-300 p-2 bg-green-50">
                      <strong>YES:</strong> Inference about the Population <strong>AND</strong> Inference about Cause and Effect.
                    </td>
                    <td className="border border-slate-300 p-2 bg-blue-50">
                      <strong>NO:</strong> Inference about Cause and Effect <strong>ONLY</strong>. (Cannot generalize to whole population).
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 p-2 font-semibold bg-slate-50">NOT Randomly Assigned?</td>
                    <td className="border border-slate-300 p-2 bg-amber-50">
                      <strong>YES:</strong> Inference about the Population <strong>ONLY</strong>. (Correlation, not Causation).
                    </td>
                    <td className="border border-slate-300 p-2 bg-red-50">
                      <strong>NO:</strong> No statistical inference can be made.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
              <li><strong>Most Experiments:</strong> Often use volunteers (not random selection) but use random assignment. Therefore, we can conclude <strong>causation</strong>, but only for <strong>people similar to those in the study</strong>.</li>
              <li><strong>Most Surveys:</strong> Use random selection but no random assignment. We can generalize to the <strong>population</strong>, but only conclude <strong>association</strong>.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Statistical Significance</h3>
            <KeyTerm 
              term="Statistical Significance" 
              definition="An observed effect so large that it would rarely occur by chance."
            />
            <p className="text-slate-700 mt-4">
              If the difference between the treatment groups is larger than what we would expect from random assignment alone, we reject the idea that "nothing is happening" and conclude the treatment caused the difference.
            </p>
          </section>
        </div>
      )
    }
  ]
};

export default statisticsUnit3Notes;
